import React from 'react';
import { Modal, ConfirmModal } from '../../../../../components';
import { KnowledgeSourceForm } from './KnowledgeSourceForm';
import KnowledgeBaseForm from '../../KnowledgeBaseList/components/KnowledgeBaseForm';
import VectorizeSourceForm from './VectorizeSourceForm';
import { SourceDetailsModal } from './SourceDetailsModal';
import type { ContentData } from './SourceDetailsModal';
import EditChunkForm from './EditChunkModal';
import { LABELS, MODAL_SIZES, ACTION_TYPES } from '../constants';
import type { ExtendedActionCardPayload, ActionType } from '../types';
import type { ActionType as BaseActionType } from '../../KnowledgeBaseList/knowledge-base-list';

type SourceModalsProps = {
  isOpened: boolean;
  isOpenedRenameModal: boolean;
  isOpenedDeleteModal: boolean;
  isOpenedActivateModal: boolean;
  isOpenedSourceModal: boolean;
  isOpenedEditChunkModal: boolean;
  onClose: () => void;
  onCloseRenameModal: () => void;
  onCloseDeleteModal: () => void;
  onCloseActivateModal: () => void;
  onCloseSourceModal: () => void;
  onCloseEditChunkModal: () => void;
  actionPayload: ExtendedActionCardPayload | null;
  setActionPayload: (payload: ExtendedActionCardPayload | null) => void;
  editingChunk: ContentData | null;
  isSourceCreating: boolean;
  deletingKnowledgeSource: boolean;
  renamingKnowledgeSource: boolean;
  activatingKnowledgeSource: boolean;
  updatingChunk: boolean;
  handleUploadSourceFile: (value: any) => void;
  handleUploadSourceText: (value: any) => void;
  handleUploadSourceFirecrawl: (value: any) => void;
  handleRenameKnowledgeSource: (id: string, name: string) => void;
  handleDeleteKnowledgeSource: (id: string) => void;
  handleActivateKnowledgeSource: (payload: any) => void;
  handleSaveEditChunk: (data: {
    metadata: Record<string, any>;
    document: string;
  }) => void;
  handleEditChunk: (chunk: ContentData) => void;
  refreshTrigger: number;
};

export const SourceModals: React.FC<SourceModalsProps> = ({
  isOpened,
  isOpenedRenameModal,
  isOpenedDeleteModal,
  isOpenedActivateModal,
  isOpenedSourceModal,
  isOpenedEditChunkModal,
  onClose,
  onCloseRenameModal,
  onCloseDeleteModal,
  onCloseActivateModal,
  onCloseSourceModal,
  onCloseEditChunkModal,
  actionPayload,
  setActionPayload,
  editingChunk,
  isSourceCreating,
  deletingKnowledgeSource,
  renamingKnowledgeSource,
  activatingKnowledgeSource,
  updatingChunk,
  handleUploadSourceFile,
  handleUploadSourceText,
  handleUploadSourceFirecrawl,
  handleRenameKnowledgeSource,
  handleDeleteKnowledgeSource,
  handleActivateKnowledgeSource,
  handleSaveEditChunk,
  handleEditChunk,
  refreshTrigger,
}) => {
  const convertToBaseActionType = (action: ActionType): BaseActionType => {
    switch (action) {
      case ACTION_TYPES.RENAME:
        return 'rename';
      case ACTION_TYPES.DELETE:
        return 'delete';
      case ACTION_TYPES.ACTIVATE:
        return 'activate';
      case ACTION_TYPES.DETAILS:
        return 'details';
      default:
        return 'rename';
    }
  };

  return (
    <>
      <ConfirmModal
        title={LABELS.DELETE_SOURCE_CONFIRMATION}
        open={isOpenedDeleteModal}
        onClose={onCloseDeleteModal}
        saving={deletingKnowledgeSource}
        onConfirm={() => handleDeleteKnowledgeSource(actionPayload?.id || '')}
      />
      <Modal
        size={MODAL_SIZES.SMALL}
        open={isOpenedRenameModal}
        onClose={onCloseRenameModal}
        title={LABELS.RENAME_SOURCE}
      >
        <KnowledgeBaseForm
          key="sourceRename"
          isSource
          onCancel={onCloseRenameModal}
          actionType={convertToBaseActionType(
            actionPayload?.action || ACTION_TYPES.RENAME
          )}
          saving={renamingKnowledgeSource}
          onSave={data =>
            handleRenameKnowledgeSource(actionPayload?.id || '', data.name)
          }
        />
      </Modal>

      <Modal
        size={MODAL_SIZES.MEDIUM}
        open={isOpened}
        onClose={onClose}
        title={LABELS.ADD_DATA_SOURCE}
      >
        <KnowledgeSourceForm
          isSourceCreating={isSourceCreating}
          handleUploadSourceFile={handleUploadSourceFile}
          handleUploadSourceText={handleUploadSourceText}
          handleUploadSourceFirecrawl={handleUploadSourceFirecrawl}
          onCancel={onClose}
        />
      </Modal>

      <Modal
        size={MODAL_SIZES.SMALL}
        open={isOpenedActivateModal}
        onClose={onCloseActivateModal}
        title={LABELS.ACTIVATE_DATA_SOURCE}
      >
        <VectorizeSourceForm
          saving={activatingKnowledgeSource}
          onSave={data => {
            handleActivateKnowledgeSource({
              dataSourceId: actionPayload?.id as string,
              ...data,
            });
          }}
          onCancel={onCloseActivateModal}
        />
      </Modal>

      <Modal
        size={MODAL_SIZES.BIG}
        open={isOpenedSourceModal}
        onClose={isOpenedEditChunkModal ? undefined : onCloseSourceModal}
        title={
          actionPayload?.action === ACTION_TYPES.DETAILS
            ? actionPayload?.name
            : LABELS.RAG_SEARCH
        }
      >
        <SourceDetailsModal
          dataSourceId={actionPayload?.id}
          type={
            actionPayload?.action === ACTION_TYPES.DETAILS ? 'view' : 'search'
          }
          onEditChunk={handleEditChunk}
          refreshTrigger={refreshTrigger}
          isModalOpen={isOpenedSourceModal}
        />
      </Modal>

      <Modal
        size={MODAL_SIZES.MEDIUM}
        open={isOpenedEditChunkModal}
        onClose={onCloseEditChunkModal}
        title={LABELS.EDIT_CHUNK_TITLE}
      >
        <EditChunkForm
          key={`edit-chunk-${editingChunk?.id || 'new'}`}
          onClose={onCloseEditChunkModal}
          onSave={handleSaveEditChunk}
          saving={updatingChunk}
          values={
            editingChunk
              ? {
                  metadata: JSON.stringify(editingChunk.metadata, null, 2),
                  text: editingChunk.content || '',
                }
              : undefined
          }
        />
      </Modal>
    </>
  );
};
